<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import camelcaseKeys from 'camelcase-keys';
import CompanyAPI from 'dashboard/api/companies';
import ContactAPI from 'dashboard/api/contacts';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { DuplicateCompanyException } from 'shared/helpers/CustomErrors';

import SidePanelDrawer from 'dashboard/components-next/SidePanelDrawer.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import CompaniesForm from 'dashboard/components-next/Companies/CompaniesForm/CompaniesForm.vue';

const props = defineProps({
  contactId: { type: Number, required: true },
  company: { type: Object, default: null },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();
const store = useStore();
const companiesStore = useCompaniesStore();

const drawerRef = ref(null);
const companiesFormRef = ref(null);
const searchResults = ref([]);
const isSearching = ref(false);
const isUpdating = ref(false);
const selectedCompanyId = ref(null);
const selectedCandidate = ref(null);
const searchQuery = ref('');

const hasCompany = computed(() => !!props.company);

const companyData = computed(() => ({
  id: props.company?.id,
  name: props.company?.name,
  domain: props.company?.domain,
  description: props.company?.description,
  email: props.company?.email,
  phoneNumber: props.company?.phone_number,
  city: props.company?.city,
  countryCode: props.company?.country_code,
  socialProfiles: props.company?.social_profiles,
  cnpj: props.company?.cnpj,
  customAttributes: props.company?.custom_attributes,
}));

const formData = ref({ ...companyData.value });

const handleFormUpdate = updated => {
  formData.value = { ...updated };
};

const close = () => drawerRef.value?.close();

const companyOptions = computed(() =>
  searchResults.value.map(c => ({
    value: c.id,
    label: [c.name, c.domain].filter(Boolean).join(' · '),
  }))
);

const emptyState = computed(() => {
  if (isSearching.value) return t('CONTACTS_LAYOUT.DETAILS.COMPANY.LOADING');
  return t('CONTACTS_LAYOUT.DETAILS.COMPANY.NO_RESULTS');
});

const doSearch = async query => {
  const q = query.trim();
  isSearching.value = true;
  try {
    const { data } = q
      ? await CompanyAPI.search(q)
      : await CompanyAPI.get({ sort: 'name' });
    searchResults.value = camelcaseKeys(data?.payload || [], { deep: true });
  } catch {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

const debouncedSearch = debounce(doSearch, 300);

const handleSearch = query => {
  searchQuery.value = query;
  debouncedSearch(query);
};

const handleCompanySelect = companyId => {
  const found = searchResults.value.find(c => c.id === Number(companyId));
  selectedCompanyId.value = null;
  if (found) selectedCandidate.value = found;
};

const clearSelectedCandidate = () => {
  selectedCandidate.value = null;
  searchQuery.value = '';
  doSearch('');
};

const refreshContact = () =>
  store.dispatch('contacts/show', { id: props.contactId });

const confirmAssign = async () => {
  if (!selectedCandidate.value || isUpdating.value) return;
  isUpdating.value = true;
  try {
    await ContactAPI.assignCompany(selectedCandidate.value.id, props.contactId);
    await refreshContact();
    emit('updated');
    useAlert(t('CONTACTS_LAYOUT.DETAILS.COMPANY.ASSIGN_SUCCESS'));
    clearSelectedCandidate();
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.COMPANY.ASSIGN_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const removeCompany = async () => {
  if (!props.company || isUpdating.value) return;
  isUpdating.value = true;
  try {
    await ContactAPI.removeCompany(props.company.id, props.contactId);
    await refreshContact();
    emit('updated');
    useAlert(t('CONTACTS_LAYOUT.DETAILS.COMPANY.REMOVE_SUCCESS'));
    doSearch('');
    close();
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.COMPANY.REMOVE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const handleSave = async () => {
  if (!props.company) return;
  const data = companiesFormRef.value?.state || formData.value;
  isUpdating.value = true;
  try {
    await companiesStore.update({
      id: props.company.id,
      name: data.name?.trim(),
      domain: data.domain?.trim() || null,
      description: data.description?.trim() || null,
      email: data.email?.trim() || null,
      phoneNumber: data.phoneNumber?.trim() || null,
      city: data.city?.trim() || null,
      countryCode: data.countryCode || null,
      socialProfiles: data.socialProfiles || {},
      cnpj: data.cnpj?.trim() || null,
      customAttributes: data.customAttributes || {},
    });
    await refreshContact();
    emit('updated');
    useAlert(t('CONTACTS_LAYOUT.DETAILS.COMPANY.UPDATE_SUCCESS'));
    close();
  } catch (error) {
    if (
      error instanceof DuplicateCompanyException &&
      error.data.includes('cnpj')
    ) {
      useAlert(
        t('COMPANIES_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.CNPJ.DUPLICATE')
      );
    } else {
      useAlert(t('CONTACTS_LAYOUT.DETAILS.COMPANY.UPDATE_ERROR'));
    }
  } finally {
    isUpdating.value = false;
  }
};

onMounted(() => {
  doSearch('');
});

const open = () => {
  formData.value = { ...companyData.value };
  clearSelectedCandidate();
  drawerRef.value?.open();
};

defineExpose({ open, close });
</script>

<template>
  <SidePanelDrawer
    ref="drawerRef"
    :title="t('EDIT_COMPANY.TITLE')"
    :description="t('EDIT_COMPANY.DESC')"
  >
    <template v-if="hasCompany" #header-action>
      <Button
        v-tooltip.top="t('CONTACTS_LAYOUT.DETAILS.COMPANY.REMOVE')"
        icon="i-lucide-unlink"
        slate
        ghost
        xs
        :disabled="isUpdating"
        @click="removeCompany"
      />
    </template>

    <div v-if="!hasCompany && !selectedCandidate" class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <span class="text-base font-semibold text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.DETAILS.COMPANY.ADD_TITLE') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('CONTACTS_LAYOUT.DETAILS.COMPANY.ADD_DESCRIPTION') }}
        </span>
      </div>
      <ComboBox
        use-api-results
        :model-value="selectedCompanyId"
        :options="companyOptions"
        :disabled="isUpdating"
        :empty-state="emptyState"
        :search-placeholder="
          t('CONTACTS_LAYOUT.DETAILS.COMPANY.SEARCH_PLACEHOLDER')
        "
        :placeholder="t('CONTACTS_LAYOUT.DETAILS.COMPANY.ADD_TITLE')"
        @search="handleSearch"
        @update:model-value="handleCompanySelect"
      />
    </div>

    <div v-else-if="selectedCandidate" class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ selectedCandidate.name }}
        </span>
        <span v-if="selectedCandidate.domain" class="text-sm text-n-slate-11">
          {{ selectedCandidate.domain }}
        </span>
      </div>
      <div class="flex items-center justify-between gap-2">
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.COMPANY.CANCEL')"
          variant="faded"
          color="slate"
          class="w-full"
          :disabled="isUpdating"
          @click="clearSelectedCandidate"
        />
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.COMPANY.CONFIRM_ADD')"
          class="w-full"
          :is-loading="isUpdating"
          :disabled="isUpdating"
          @click="confirmAssign"
        />
      </div>
    </div>

    <template v-else>
      <CompaniesForm
        ref="companiesFormRef"
        :company-data="companyData"
        @update="handleFormUpdate"
      />

      <div class="flex items-center w-full gap-3 mt-6">
        <Button
          :label="t('EDIT_COMPANY.ACTIONS.UPDATE')"
          color="blue"
          :is-loading="isUpdating"
          @click="handleSave"
        />
        <Button
          :label="t('EDIT_COMPANY.ACTIONS.CANCEL')"
          variant="faded"
          color="slate"
          @click="close"
        />
      </div>
    </template>
  </SidePanelDrawer>
</template>
